// src/components/common/PersonalisePanel.jsx
import { useState } from "react";

/**
 * Inline personalise control.
 * Idle state   → small "Personalise It" button.
 * Active state → container widens and slides to reveal
 *                "Write Your Name Here" input + "Add to bag".
 *
 * Name is OPTIONAL — user can type a name or leave it blank and
 * still add the product to the bag either way.
 *
 * Props:
 *  - onAddToCart: ({ name }) => void   called when "Add to bag" is clicked
 *  - onNameChange: (name) => void      called on every keystroke, so the
 *                                      parent can show a live preview on
 *                                      the product image while the user types
 *  - onActiveChange: (active) => void  called when the panel opens/closes, so
 *                                      the parent can show/hide the font,
 *                                      colour & position pickers accordingly
 */
export default function PersonalisePanel({ onAddToCart, onNameChange, onActiveChange }) {
  const [active, setActive] = useState(false);
  const [name, setName] = useState("");

  const openPanel = () => {
    setActive(true);
    onActiveChange?.(true);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setName(val);
    onNameChange?.(val);
  };

  const handleAdd = () => {
    onAddToCart({ name: name.trim() });
    setActive(false);
    setName("");
    onNameChange?.("");
    onActiveChange?.(false);
  };

  return (
    <div
      className={`relative h-[52px] rounded-md border border-[#1A1A1A] overflow-hidden transition-[max-width] duration-500 ease-in-out ${
        active ? "max-w-[300px] sm:max-w-[420px]" : "max-w-[250px]"
      } w-full`}
    >
      {/* Idle button — slides fully out to the left when active */}
      <button
        onClick={openPanel}
        className={`absolute inset-0 w-full h-full bg-[#1A1A1A] text-white text-sm font-semibold transition-transform duration-500 ease-in-out ${
          active ? "-translate-x-full" : "translate-x-0"
        }`}
      >
        Personalise It
      </button>

      {/* Name input + Add to bag — slides in from the right when active */}
      <div
        className={`absolute inset-0 flex transition-transform duration-500 ease-in-out ${
          active ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <input
          type="text"
          value={name}
          onChange={handleChange}
          placeholder="Write Your Name Here (optional)"
          className="flex-1 min-w-0 h-full px-3 sm:px-4 text-sm font-semibold outline-none"
        />
        {/* Name is optional now — button is always clickable, with or without a name */}
        <button
          onClick={handleAdd}
          className="h-full bg-[#1A1A1A] text-white px-3 sm:px-4 text-sm font-semibold whitespace-nowrap transition-opacity hover:opacity-90"
        >
          Add to bag
        </button>
      </div>
    </div>
  );
}